import React, { useCallback } from "react";
import { graphql } from "react-relay";
import {
	components,
	type DropdownIndicatorProps,
	type MultiValueGenericProps,
	type OnChangeValue,
	type OptionProps,
	type SingleValueProps,
	type StylesConfig,
	type Theme,
} from "react-select";
import AsyncSelect from "react-select/async";

import {
	faAddressCard,
	faBuilding,
	faFileInvoice,
	faSuitcase,
	faUserCircle,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";
import { fetchQuery } from "relay-runtime";

import { zindexModal } from "~/css/zindexes";
import { environment } from "~/models";
import fellowSelectProps from "~/ui/react-select";
import { IndicatorSeparator, outlinedStyles } from "~/ui/selectors/SelectorUtils";

import type {
	SalesforceSearchType,
	SalesforceSelectSearchQuery,
	SalesforceSelectSearchQuery$data,
} from "~/__generated__/SalesforceSelectSearchQuery.graphql";

import styles from "./css/SalesforceSendNotes.module.css";

type SalesforceSearchOptionTypeWithOther = SalesforceSelectSearchQuery$data["salesforceSearch"][number];
type SalesforceSearchOptionType = Exclude<
	SalesforceSearchOptionTypeWithOther,
	Extract<SalesforceSearchOptionTypeWithOther, { __typename: "%other" }>
>;

export type SalesforceOptionType = SalesforceSearchOptionType;

export type SalesforceContactNodeType = Extract<SalesforceSearchOptionType, { __typename: "SalesforceContactNode" }>;
export type SalesforceAccountNodeType = Extract<SalesforceSearchOptionType, { __typename: "SalesforceAccountNode" }>;
export type SalesforceOpportunityNodeType = Extract<
	SalesforceSearchOptionType,
	{ __typename: "SalesforceOpportunityNode" }
>;
export type SalesforceCaseNodeType = Extract<SalesforceSearchOptionType, { __typename: "SalesforceCaseNode" }>;
export type SalesforceLeadNodeType = Extract<SalesforceSearchOptionType, { __typename: "SalesforceLeadNode" }>;

const salesforceSearch = graphql`
	query SalesforceSelectSearchQuery($text: String!, $searchType: SalesforceSearchType!) {
		salesforceSearch(text: $text, searchType: $searchType) {
			__typename
			... on SalesforceContactNode {
				id
				url
				name
				title
				email
			}
			... on SalesforceAccountNode {
				id
				url
				name
			}
			... on SalesforceOpportunityNode {
				id
				url
				name
				amount
			}
			... on SalesforceCaseNode {
				id
				url
				subject
				caseNumber
				description
			}
			... on SalesforceLeadNode {
				id
				url
				name
				company
			}
		}
	}
`;

function SalesforceItem({
	logo,
	title,
	primary,
	secondary,
}: {
	logo: React.ReactNode;
	title: string;
	primary: string;
	secondary?: string | null;
}) {
	return (
		<div className={styles.searchOption}>
			<div className={styles.logoContainer}>
				<div className={styles.logo}>{logo}</div>
			</div>
			<div className={styles.optionText}>
				<div className={styles.optionTitle}>{title}</div>
				<div>
					{primary}
					{secondary ? ` · ${secondary}` : ""}
				</div>
			</div>
		</div>
	);
}

const SingleValue = (props: SingleValueProps<SalesforceOptionType, boolean>) => {
	const data = props.data as SalesforceOptionType;
	const { children: _children, ...rest } = props;
	let content;

	switch (data.__typename) {
		case "SalesforceAccountNode":
			content = `Account · ${data.name}`;
			break;
		case "SalesforceCaseNode":
			content = `Case · ${data.caseNumber}`;
			break;
		case "SalesforceContactNode":
			content = `Contact · ${data.name}`;
			break;
		case "SalesforceLeadNode":
			content = `Lead · ${data.name}`;
			break;
		case "SalesforceOpportunityNode":
			content = `Opportunity · ${data.name}`;
			break;
	}

	return (
		<components.SingleValue {...rest} key={`option-${data.id}`}>
			{content}
		</components.SingleValue>
	);
};

const MultiValueLabel = (props: MultiValueGenericProps<SalesforceOptionType, boolean>) => {
	const data = props.data as SalesforceOptionType;
	const { children: _children, ...rest } = props;
	let content;

	switch (data.__typename) {
		case "SalesforceAccountNode":
			content = `Account · ${data.name}`;
			break;
		case "SalesforceCaseNode":
			content = `Case · ${data.caseNumber}`;
			break;
		case "SalesforceContactNode":
			content = `Contact · ${data.name}`;
			break;
		case "SalesforceLeadNode":
			content = `Lead · ${data.name}`;
			break;
		case "SalesforceOpportunityNode":
			content = `Opportunity · ${data.name}`;
			break;
	}

	return (
		<components.MultiValueLabel {...rest} key={`option-${data.id}`}>
			{content}
		</components.MultiValueLabel>
	);
};

const DropdownIndicator = <T extends {}>({ children: _children, ...rest }: DropdownIndicatorProps<T, boolean>) => {
	return (
		<components.DropdownIndicator {...rest}>
			<FontAwesomeIcon icon={faCaretDown} />
		</components.DropdownIndicator>
	);
};

const Option = (props: OptionProps<SalesforceOptionType, boolean>) => {
	const data = props.data as SalesforceOptionType;
	let content;

	switch (data.__typename) {
		case "SalesforceAccountNode":
			content = (
				<SalesforceItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faBuilding} />}
					title={data.name}
					primary={"Account"}
				/>
			);
			break;
		case "SalesforceCaseNode":
			content = (
				<SalesforceItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faSuitcase} />}
					title={data.caseNumber}
					primary={"Case"}
					secondary={data.subject}
				/>
			);
			break;
		case "SalesforceContactNode":
			content = (
				<SalesforceItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faAddressCard} />}
					title={data.name}
					primary={"Contact"}
				/>
			);
			break;
		case "SalesforceLeadNode":
			content = (
				<SalesforceItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faUserCircle} />}
					title={data.name}
					primary={"Lead"}
				/>
			);
			break;
		case "SalesforceOpportunityNode":
			content = (
				<SalesforceItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faFileInvoice} />}
					title={data.name}
					primary={"Opportunity"}
				/>
			);
			break;
	}

	return (
		<div
			{...props.innerProps}
			key={`option-${data.id}`}
			className={props.cx(
				{
					option: true,
					"option--is-disabled": props.isDisabled,
					"option--is-focused": props.isFocused,
					"option--is-selected": props.isSelected,
					"custom--style": true,
				},
				props.className,
			)}
		>
			{content}
		</div>
	);
};

function reactSelectTheme(theme: Theme) {
	return {
		...theme,
		colors: {
			...theme.colors,
			primary25: "var(--royal100)",
			primary: "var(--royal400)",
		},
	};
}

function getOptionValue(item: SalesforceOptionType) {
	return item.id;
}

function handleLoadingMessage({ inputValue }: { inputValue: string }) {
	return inputValue.length < 2 ? "Please type more than 1 character" : "Loading…";
}

function handleNoOptionMessage({ inputValue }: { inputValue: string }) {
	return inputValue.length < 2 ? "Please type more than 1 character" : "No results found";
}

type SalesforceSelectProps = {
	setSelectedOption: (id: readonly SalesforceOptionType[] | null) => void;
	searchType: SalesforceSearchType;
	isMulti?: boolean;
	options?: readonly SalesforceOptionType[];
	/*
	 * defaultValue is only used when isMulti === False, so we will keep SalesforceOptionType
	 * instead of using SalesforceOptionType[]
	 */
	defaultValue?: SalesforceOptionType;
	placeholder?: string;
};

export function SalesforceSelect({
	setSelectedOption,
	options = [],
	searchType,
	defaultValue = undefined,
	placeholder,
	isMulti = false,
}: SalesforceSelectProps): JSX.Element {
	const [inputValue, setInputValue] = React.useState("");
	const [value, setValue] = React.useState<SalesforceOptionType | undefined>(defaultValue);

	const loadOptions = React.useCallback(
		(inputValue: string, callback) => {
			if (inputValue.length < 2) {
				callback([]);
				return;
			}
			fetchQuery<SalesforceSelectSearchQuery>(environment, salesforceSearch, { text: inputValue, searchType })
				.toPromise()
				.then(data => {
					if (!data) {
						console.error("Unable to fetch options from salesforce");
						return;
					}
					callback(data.salesforceSearch.filter(item => item.__typename !== "%other"));
				});
		},
		[searchType],
	);

	const debounceLoadOptions = React.useMemo(() => debounce(loadOptions, 250), [loadOptions]);

	const handleInputChange = useCallback((val: string) => {
		setInputValue(val);
	}, []);

	const onChangeOption = React.useCallback(
		(option: OnChangeValue<SalesforceOptionType, boolean>) => {
			if (option && "length" in option) {
				setSelectedOption(option || []);
			} else {
				setValue(option ? option : undefined);
				setSelectedOption(option ? [option] : null);
			}
		},
		[setSelectedOption],
	);

	const selectorStyles: StylesConfig = {
		...outlinedStyles,
		menuPortal: base => ({ ...base, zIndex: zindexModal + 1 }),
		multiValue: provided => ({
			...provided,
			background: "var(--thirdLayerColor)",
			color: "var(--vividGrey)",
			borderRadius: "14px",
			cursor: "default",
			position: "relative",
			paddingRight: "25px",
		}),
		multiValueRemove: provided => ({
			...provided,
			cursor: "pointer",
			backgroundColor: "transparent",
			position: "absolute",
			right: "0px",
			top: "0px",
			height: "19px",
			borderRadius: "50%",
			paddingLeft: "3.5px",
			paddingRight: "3.5px",
			":hover": {
				background: "var(--mutedGrey)",
				color: "var(--placeholderTextColor)",
			},
		}),
	};

	return (
		<AsyncSelect<SalesforceOptionType, boolean>
			{...fellowSelectProps}
			className={cx(styles.salesforceSelect, fellowSelectProps.className)}
			cacheOptions
			defaultOptions={options}
			placeholder={placeholder ?? "Select an object in Salesforce to send these notes…"}
			isSearchable
			loadOptions={debounceLoadOptions}
			components={{ Option, SingleValue, DropdownIndicator, IndicatorSeparator, MultiValueLabel }}
			isClearable
			inputValue={inputValue}
			onInputChange={handleInputChange}
			getOptionValue={getOptionValue}
			value={value}
			onChange={onChangeOption}
			loadingMessage={handleLoadingMessage}
			noOptionsMessage={handleNoOptionMessage}
			theme={reactSelectTheme}
			menuPlacement="auto"
			menuPortalTarget={document.body}
			menuPosition="fixed"
			styles={selectorStyles as StylesConfig<SalesforceOptionType, boolean>}
			isMulti={isMulti}
		/>
	);
}
